// MARK://dhk8/admin-shell/pages/PaymentsSettingsPage
// Configuración de pagos digitales del negocio (tenant actual). Tres secciones:
//   A. Métodos de pago  — efectivo / terminal / pago en línea (gate del tenant).
//   B. Procesador (Stripe) — llaves pk_/sk_/whsec_ + activar; secretos write-only.
//   C. Webhook — endpoint informativo que el comercio pega en su panel de Stripe.
// El entorno TEST/LIVE del admin ya determina qué tenant/config está activo — NO
// hay pestañas test/live aquí; `mode` se deriva de `session.activeEnv`.
// Fuente: dhk8-payments-gateway (REST, sobre { ok, data }).
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CreditCard, Banknote, Smartphone, Globe, Save, Copy, Webhook, ChefHat } from 'lucide-react'
import { useAuthStore } from '../stores/auth-store.js'
import {
  getPaymentMethods, setPaymentMethods,
  getPaymentConfigSummary, savePaymentConfig,
  type PaymentMethods, type PaymentConfigSummary,
} from '../lib/clients/payments.js'

// El comercio pega ESTE endpoint fijo en su panel de Stripe (evento
// payment_intent.succeeded). Es el ingress público, no el api-origin del admin.
const WEBHOOK_URL = 'https://api.nintaigo.com/webhooks/stripe'

// Compuerta de gerente para acciones sensibles (guardar config de pagos).
// TODO: gate tras PIN de gerente. El POS ya tiene el patrón completo en
// apps/pos/src/features/security/pin-gate-store.ts (`requireManagerAuth(action)` →
// verifica contra dhk8-platform-auth /v1/auth/manager-verify {pinId,pin}; se salta
// el prompt para owner/admin/manager). admin-shell aún no monta ese overlay, así
// que por ahora este único chokepoint deja pasar. Cuando se porte el sheet, abrir
// aquí el prompt y sólo llamar `run()` si `proceed` es true.
async function guardManagerAction(run: () => Promise<void>): Promise<void> {
  await run()
}

export default function PaymentsSettingsPage() {
  const activeEnv = useAuthStore((s) => s.session?.activeEnv ?? 'test')

  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)

  // A. Métodos
  const [cashEnabled, setCashEnabled] = useState(true)
  const [terminalEnabled, setTerminalEnabled] = useState(false)
  const [onlineEnabled, setOnlineEnabled] = useState(false)
  const [fireOnPrepay, setFireOnPrepay] = useState(true)
  const [methodsSaving, setMethodsSaving] = useState(false)
  const [methodsErr, setMethodsErr] = useState<string | null>(null)
  const [methodsOk, setMethodsOk] = useState(false)

  // B. Procesador (Stripe)
  const [summary, setSummary] = useState<PaymentConfigSummary | null>(null)
  const [publishableKey, setPublishableKey] = useState('')
  const [secretKey, setSecretKey] = useState('')       // write-only (leave-blank-to-keep)
  const [webhookSecret, setWebhookSecret] = useState('') // write-only (leave-blank-to-keep)
  const [isActive, setIsActive] = useState(false)
  const [configSaving, setConfigSaving] = useState(false)
  const [configErr, setConfigErr] = useState<string | null>(null)
  const [configOk, setConfigOk] = useState(false)

  function hydrateMethods(m: PaymentMethods) {
    setCashEnabled(m.cashEnabled)
    setTerminalEnabled(m.terminalEnabled)
    setOnlineEnabled(m.onlineEnabled)
    setFireOnPrepay(m.fireOnPrepay)
  }
  function hydrateConfig(c: PaymentConfigSummary | null) {
    setSummary(c)
    setPublishableKey(c?.publishableKey ?? '')
    setIsActive(c?.isActive ?? false)
    // Los secretos nunca vuelven del backend → los inputs arrancan vacíos.
    setSecretKey('')
    setWebhookSecret('')
  }

  useEffect(() => {
    let alive = true
    Promise.all([getPaymentMethods(), getPaymentConfigSummary()])
      .then(([m, c]) => { if (alive) { hydrateMethods(m); hydrateConfig(c); setLoading(false) } })
      .catch((e) => { if (alive) { setLoadErr(e instanceof Error ? e.message : 'No se pudo cargar.'); setLoading(false) } })
    return () => { alive = false }
  }, [])

  async function saveMethods() {
    setMethodsOk(false)
    setMethodsErr(null)
    await guardManagerAction(async () => {
      setMethodsSaving(true)
      try {
        // Si el negocio activa "pago en línea" sin un Stripe activo, el backend
        // responde VALIDATION_ERROR → lo mostramos tal cual en el banner.
        const next = await setPaymentMethods({ cashEnabled, terminalEnabled, onlineEnabled, fireOnPrepay })
        hydrateMethods(next)
        setMethodsOk(true)
      } catch (e) {
        setMethodsErr(e instanceof Error ? e.message : 'No se pudieron guardar los métodos.')
      } finally {
        setMethodsSaving(false)
      }
    })
  }

  async function saveConfig() {
    setConfigOk(false)
    setConfigErr(null)
    await guardManagerAction(async () => {
      setConfigSaving(true)
      try {
        // Sólo enviamos los secretos que el usuario capturó (leave-blank-to-keep).
        // Para Stripe, secretKey/webhookSecret viajan dentro de `credentials`.
        const credentials: Record<string, string> = {}
        if (secretKey.trim()) credentials.secretKey = secretKey.trim()
        if (webhookSecret.trim()) credentials.webhookSecret = webhookSecret.trim()
        const next = await savePaymentConfig({
          processor: 'stripe',
          mode: activeEnv, // = entorno activo del admin (test | live)
          ...(publishableKey.trim() ? { publishableKey: publishableKey.trim() } : {}),
          credentials,
          isActive,
        })
        hydrateConfig(next)
        setConfigOk(true)
      } catch (e) {
        setConfigErr(e instanceof Error ? e.message : 'No se pudo guardar el procesador.')
      } finally {
        setConfigSaving(false)
      }
    })
  }

  function copy(text: string) { void navigator.clipboard?.writeText(text) }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Pagos</h1>
          <p className="adm-page__sub">Configura los métodos de pago y el procesador de pagos en línea</p>
        </div>
        <div className="adm-page__actions">
          <Link to="/dashboard" className="adm-btn adm-btn--sm">← Tablero</Link>
        </div>
      </div>

      {loading && <div className="adm-card">Cargando…</div>}
      {loadErr && <div className="adm-banner adm-banner--danger">{loadErr}</div>}

      {!loading && !loadErr && (
        <>
          {/* ── A. Métodos de pago ─────────────────────────────────────────── */}
          <div className="adm-card">
            <h2 className="adm-card__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CreditCard size={16} aria-hidden /> Métodos de pago
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ToggleRow
                Icon={Banknote}
                title="Efectivo"
                desc="Cobro en efectivo en el mostrador."
                on={cashEnabled}
                onToggle={() => setCashEnabled((v) => !v)}
              />
              <ToggleRow
                Icon={Smartphone}
                title="Terminal"
                desc="Cobro con terminal física (tarjeta presente)."
                on={terminalEnabled}
                onToggle={() => setTerminalEnabled((v) => !v)}
              />
              <ToggleRow
                Icon={Globe}
                title="Pago en línea"
                desc="Requiere un procesador (Stripe) activo y configurado abajo."
                on={onlineEnabled}
                onToggle={() => setOnlineEnabled((v) => !v)}
              />
              {/* Fire-timing del prepago — sólo relevante si el pago en línea está
                  activo (una orden para llevar se prepaga online). */}
              {onlineEnabled && (
                <div style={{ paddingLeft: 26, borderLeft: '2px solid var(--adm-line, #e5e7eb)' }}>
                  <ToggleRow
                    Icon={ChefHat}
                    title="Enviar a cocina al prepagar"
                    desc={fireOnPrepay
                      ? 'Activado: la orden para llevar entra a cocina en cuanto se cobra en línea.'
                      : 'Desactivado: la orden prepagada se retiene hasta que el staff la acepte y la envíe a cocina.'}
                    on={fireOnPrepay}
                    onToggle={() => setFireOnPrepay((v) => !v)}
                  />
                </div>
              )}
            </div>

            {methodsErr && <div className="adm-banner adm-banner--danger" style={{ marginTop: 12 }}>{methodsErr}</div>}
            {methodsOk && <div className="adm-banner" style={{ marginTop: 12 }}>Métodos de pago guardados.</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="adm-btn adm-btn--primary" onClick={saveMethods} disabled={methodsSaving}>
                <Save size={16} aria-hidden /> {methodsSaving ? 'Guardando…' : 'Guardar métodos'}
              </button>
            </div>
          </div>

          {/* ── B. Procesador de pago (Stripe) ─────────────────────────────── */}
          <div className="adm-card">
            <h2 className="adm-card__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CreditCard size={16} aria-hidden /> Procesador de pago (Stripe)
            </h2>

            {/* Resumen de sólo lectura del estado actual. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <span className="adm-badge adm-badge--muted">Stripe</span>
              <span className="adm-badge adm-badge--info">{summary?.mode === 'live' ? 'En vivo' : 'Pruebas'}</span>
              <span className={`adm-badge ${summary?.isActive ? 'adm-badge--ok' : 'adm-badge--muted'}`}>
                {summary?.isActive ? 'Activo' : 'Inactivo'}
              </span>
              {summary?.publishableKey && (
                <span className="adm-row__meta adm-mono">{summary.publishableKey}</span>
              )}
              {!summary && <span className="adm-row__meta">Sin configurar todavía.</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
              <label className="adm-field">
                <span className="adm-field__label">Clave publicable (pk_…)</span>
                <input
                  className="adm-input adm-mono"
                  value={publishableKey}
                  placeholder="pk_live_…"
                  autoComplete="off"
                  onChange={(e) => setPublishableKey(e.target.value)}
                />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Clave secreta (sk_…)</span>
                <input
                  className="adm-input adm-mono"
                  type="password"
                  autoComplete="off"
                  value={secretKey}
                  placeholder="dejar vacío para no cambiarlo"
                  onChange={(e) => setSecretKey(e.target.value)}
                />
              </label>
              <label className="adm-field">
                <span className="adm-field__label">Signing secret del webhook (whsec_…)</span>
                <input
                  className="adm-input adm-mono"
                  type="password"
                  autoComplete="off"
                  value={webhookSecret}
                  placeholder="dejar vacío para no cambiarlo"
                  onChange={(e) => setWebhookSecret(e.target.value)}
                />
              </label>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{isActive ? 'Procesador activo' : 'Procesador inactivo'}</div>
                  <p className="adm-page__sub" style={{ margin: '2px 0 0' }}>
                    Activa Stripe para poder cobrar en línea.
                  </p>
                </div>
                <button
                  type="button"
                  className={isActive ? 'adm-btn adm-btn--primary' : 'adm-btn'}
                  onClick={() => setIsActive((v) => !v)}
                  aria-pressed={isActive}
                >
                  {isActive ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>

            {configErr && <div className="adm-banner adm-banner--danger" style={{ marginTop: 12 }}>{configErr}</div>}
            {configOk && <div className="adm-banner" style={{ marginTop: 12 }}>Configuración del procesador guardada.</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="adm-btn adm-btn--primary" onClick={saveConfig} disabled={configSaving}>
                <Save size={16} aria-hidden /> {configSaving ? 'Guardando…' : 'Guardar procesador'}
              </button>
            </div>
          </div>

          {/* ── C. Webhook (informativo) ───────────────────────────────────── */}
          <div className="adm-card">
            <h2 className="adm-card__title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Webhook size={16} aria-hidden /> Webhook
            </h2>
            <label className="adm-field">
              <span className="adm-field__label">URL del endpoint</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="adm-input adm-mono" style={{ flex: '1 1 320px' }} value={WEBHOOK_URL} readOnly />
                <button type="button" className="adm-btn adm-btn--sm" onClick={() => copy(WEBHOOK_URL)}>
                  <Copy size={14} aria-hidden /> Copiar
                </button>
              </div>
            </label>
            <p className="adm-row__meta" style={{ marginTop: 8 }}>
              Configura este endpoint en tu panel de Stripe (evento <code>payment_intent.succeeded</code>) y pega
              aquí el signing secret (whsec) en el campo de arriba.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

// ── Fila de método de pago (toggle on/off, mismo patrón que LoyaltyConfigPage) ──
function ToggleRow({ Icon, title, desc, on, onToggle }: {
  Icon: typeof CreditCard
  title: string
  desc: string
  on: boolean
  onToggle: () => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon size={16} aria-hidden /> {title}
        </div>
        <p className="adm-page__sub" style={{ margin: '2px 0 0' }}>{desc}</p>
      </div>
      <button
        type="button"
        className={on ? 'adm-btn adm-btn--primary adm-btn--sm' : 'adm-btn adm-btn--sm'}
        onClick={onToggle}
        aria-pressed={on}
      >
        {on ? 'Activado' : 'Desactivado'}
      </button>
    </div>
  )
}
